import type { ComboboxItem } from '@/components'
import {
  Button,
  Card,
  StickyFilters,
  Combobox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  Input,
  UserCombobox,
  useExpirationStatusLabels,
} from '@/components'
import VehicleSelect from '@/components/vehicle-select'
import { cn } from '@/lib/utils'
import { expirationStatuses } from '@/modules/common/expiration-status'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { hasActiveVehicleListFilter } from './filter-variables'
import type { VehicleListFilterForm } from './types'

interface Props {
  filter: VehicleListFilterForm
  /** Page-specific filter controls, rendered after the shared ones. */
  children?: ReactNode
  /**
   * Whether a page-specific filter (rendered via `children`) is narrowing the
   * list. OR-ed with the shared filters to decide if "clear" is offered.
   */
  extraFilterActive?: boolean
  searchPlaceholder: string
  /**
   * Whether to offer the derived expiry status (expired / expiring soon /
   * valid / missing). Off for lists whose rows have a status of their own — a
   * service is planned or booked, not expiring.
   */
  showExpirationStatus?: boolean
  /** Label for the date range. Defaults to the expiration date. */
  dateRangeLabel?: string

  onChange(patch: Partial<VehicleListFilterForm>): void
  onClear(): void
}

export const VehicleListFilters: React.FC<Props> = ({
  filter,
  children,
  extraFilterActive = false,
  searchPlaceholder,
  showExpirationStatus = true,
  dateRangeLabel,

  onChange,
  onClear,
}) => {
  const translations = useTranslate({
    filter: 'common.filter',
    search: 'common.search-filter',
    vehicles: 'common.vehicles',
    responsibleUser: 'common.responsible-user',
    status: 'common.status',
    expirationDate: 'documents.expiration-date',
    select: 'common.select-ellipsis',
    clear: 'common.clear-filters',
  })

  const statusLabels = useExpirationStatusLabels()
  const statusItems = useMemo<
    ComboboxItem<VehicleListFilterForm['expirationStatus']>[]
  >(
    () =>
      expirationStatuses.map(status => ({
        value: status,
        label: statusLabels[status],
      })),
    [statusLabels]
  )

  const [collapsed, setCollapsed] = useState(isMobileOnly)
  const showFilters = !isMobileOnly || !collapsed

  const canClear = hasActiveVehicleListFilter(filter) || extraFilterActive

  return (
    <StickyFilters>
      <Card className="mb-4 p-0" contentClassName="p-0">
        {isMobileOnly && (
          <button
            type="button"
            className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-stone-600"
            aria-expanded={showFilters}
            onClick={() => setCollapsed(value => !value)}
          >
            {translations.filter}
            <FAIcon
              className={cn(
                'transition-transform',
                showFilters && 'rotate-180'
              )}
              icon="chevron-down"
            />
          </button>
        )}

        {showFilters && (
          <form
            className={cn(
              'grid gap-4 p-4',
              'md:flex md:flex-wrap md:items-end'
            )}
            onSubmit={event => event.preventDefault()}
          >
            <Field className="md:w-[320px]">
              <FieldLabel htmlFor="vehicle-list-search">
                {translations.search}
              </FieldLabel>
              <Input
                id="vehicle-list-search"
                type="search"
                value={filter.q}
                placeholder={searchPlaceholder}
                onChange={value => onChange({ q: value })}
              />
            </Field>

            <Field>
              <FieldLabel>{translations.vehicles}</FieldLabel>
              <VehicleSelect
                mode="multiple"
                value={filter.vehicles}
                className="max-w-[350px]"
                placeholder={translations.select}
                onChange={vehicles =>
                  onChange({ vehicles: vehicles.map(vehicle => vehicle.id) })
                }
              />
            </Field>

            {children}

            {showExpirationStatus && (
              <Field>
                <FieldLabel>{translations.status}</FieldLabel>
                <Combobox
                  mode="single"
                  value={filter.expirationStatus}
                  items={statusItems}
                  clearable
                  noSearch
                  placeholder={translations.select}
                  onChange={value => onChange({ expirationStatus: value })}
                />
              </Field>
            )}

            <Field>
              <FieldLabel>
                {dateRangeLabel ?? translations.expirationDate}
              </FieldLabel>
              <DatePicker
                mode="range"
                value={filter.dateRange}
                format="PP"
                clearable
                placeholder={translations.select}
                onChange={value => onChange({ dateRange: value })}
              />
            </Field>

            <Field>
              <FieldLabel>{translations.responsibleUser}</FieldLabel>
              <UserCombobox
                mode="multiple"
                value={filter.responsibleUsers}
                className="max-w-[350px]"
                placeholder={translations.select}
                onChange={value => onChange({ responsibleUsers: value })}
              />
            </Field>

            {canClear && (
              <Button variant="ghost" size="lg" type="button" onClick={onClear}>
                <FAIcon icon="times" />
                {translations.clear}
              </Button>
            )}
          </form>
        )}
      </Card>
    </StickyFilters>
  )
}
